// ---------------------------------------------------------------------------
// DSS-36: Collapsible section wrapper for Style Panel groups
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

// ---------------------------------------------------------------------------
// localStorage persistence helpers
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = "ease-style-section-";

function readCollapsed(sectionId: string): boolean {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${sectionId}`);
    return raw === "true";
  } catch {
    return false;
  }
}

function writeCollapsed(sectionId: string, isCollapsed: boolean): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${sectionId}`, String(isCollapsed));
  } catch {
    // storage full or unavailable — silent
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StylePanelSectionProps {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}

export function StylePanelSection({
  id,
  label,
  icon: Icon,
  children,
  defaultCollapsed = false,
}: StylePanelSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => readCollapsed(id) || defaultCollapsed);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  const headingId = useId();
  const regionId = useId();

  // Measure content height for smooth transition
  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.contentRect.height);
      }
    });

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(id, next);
      return next;
    });
  }, [id]);

  return (
    <div
      style={{
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      {/* Header */}
      <button
        id={headingId}
        type="button"
        aria-expanded={!isCollapsed}
        aria-controls={regionId}
        onClick={toggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          padding: "8px 12px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "var(--color-foreground)",
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: "16px",
          textAlign: "left",
        }}
      >
        <ChevronRight
          size={14}
          style={{
            transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
            transition: "transform 200ms ease",
            flexShrink: 0,
            color: "var(--color-muted-foreground)",
          }}
        />
        <Icon size={14} />
        <span style={{ flex: 1 }}>{label}</span>
      </button>

      {/* Collapsible body */}
      <div
        id={regionId}
        role="region"
        aria-labelledby={headingId}
        style={{
          height: isCollapsed ? 0 : contentHeight !== undefined ? contentHeight : "auto",
          overflow: "hidden",
          transition: "height 200ms ease",
        }}
      >
        <div ref={contentRef} style={{ padding: "4px 12px 12px 34px" }}>
          {children}
        </div>
      </div>
    </div>
  );
}
